import React, { useCallback, useRef, useState } from "react";

/**
 * DialerKey — instant-feedback dialpad button optimised for iOS Safari and
 * Android Chrome.
 *
 * Why a dedicated component (instead of a styled <button>):
 *   • iOS Safari adds a ~300 ms delay before `click` fires unless we use
 *     `pointer*` events. This delay makes a dialpad feel sluggish.
 *   • Real phones BEEP on press — not on release. So digit-mode wires the
 *     handler to `pointerdown`. Action buttons (Call / Hangup) should still
 *     fire on release to avoid accidents — pass `mode="action"` for those.
 *   • We add visual + haptic feedback synchronously on pointerdown so the
 *     user sees a press response in the same frame the touch lands.
 *   • The native browser tap highlight (gray flash on iOS) is suppressed so
 *     the press effect we apply is clean.
 *
 * Usage:
 *   <DialerKey onPress={() => onDigit("1")}>1</DialerKey>            // digit
 *   <DialerKey mode="action" onPress={onCall}>...</DialerKey>        // commit-on-release
 *
 * Implementation notes:
 *   • Uses Pointer Events (supported in all modern browsers + iOS 13+).
 *   • Calls `event.preventDefault()` on `pointerdown` to suppress the
 *     synthetic mousedown/click that iOS would otherwise emit later — this
 *     is what eliminates the 300 ms delay AND prevents double-fire if a
 *     parent has its own click listener.
 *   • Pointer capture ensures we still get the up event even if the user's
 *     finger drifts off the button (so visuals reset cleanly).
 *   • A 10 ms vibrate is a useful tactile cue on Android / iOS 13+ devices
 *     that support `navigator.vibrate`. Silent fallback on iOS Safari.
 */
const DialerKey = React.forwardRef(function DialerKey(
  {
    onPress,
    children,
    className = "",
    disabled = false,
    mode = "digit", // "digit" | "action"
    haptic = true,
    "data-testid": testId,
    ...rest
  },
  ref
) {
  const [pressed, setPressed] = useState(false);
  // Track if the press originated on this element so a stray pointerup
  // (after pointercancel / drag-out) doesn't accidentally trigger an action.
  const downRef = useRef(false);

  const triggerHaptic = useCallback(() => {
    if (!haptic) return;
    try {
      if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
        navigator.vibrate(8);
      }
    } catch {
      /* ignore — Safari throws on some private modes */
    }
  }, [haptic]);

  const handlePointerDown = useCallback((e) => {
    if (disabled) return;
    // Capture the pointer so we get a guaranteed up/cancel even if the
    // finger slides off the button, keeping our state consistent.
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* ignore */ }
    // Suppress the synthetic 300 ms-delayed click on iOS — the most
    // important line in this component.
    if (e.cancelable) e.preventDefault();
    downRef.current = true;
    setPressed(true);
    triggerHaptic();
    if (mode === "digit") {
      // Real-phone behaviour: act on press, not release.
      onPress?.(e);
    }
  }, [disabled, mode, onPress, triggerHaptic]);

  const handlePointerUp = useCallback((e) => {
    if (disabled) return;
    setPressed(false);
    if (!downRef.current) return;
    downRef.current = false;
    if (mode === "action") {
      onPress?.(e);
    }
  }, [disabled, mode, onPress]);

  const handlePointerCancel = useCallback(() => {
    setPressed(false);
    downRef.current = false;
  }, []);

  const handlePointerLeave = useCallback(() => {
    // Visual reset only — we still want pointerup to fire for action mode
    // because we have pointer capture.
    setPressed(false);
  }, []);

  return (
    <button
      ref={ref}
      type="button"
      // Pointer event handlers (primary path on modern browsers + iOS).
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onPointerLeave={handlePointerLeave}
      // Suppress the synthesised 'click' so action mode doesn't double-fire.
      // pointer events already provided the result we need.
      onClick={(e) => { e.preventDefault(); }}
      // Prevent the iOS context-menu long-press / text selection / image drag.
      onContextMenu={(e) => e.preventDefault()}
      disabled={disabled}
      data-testid={testId}
      data-pressed={pressed ? "true" : "false"}
      // touch-action: manipulation kills the 300ms double-tap delay on
      // iOS Safari ≥ 9.3. -webkit-tap-highlight-color: transparent removes
      // the gray flash so our own press visual reads cleanly.
      style={{
        touchAction: "manipulation",
        WebkitTapHighlightColor: "transparent",
        WebkitUserSelect: "none",
        userSelect: "none",
        WebkitTouchCallout: "none",
        cursor: disabled ? "default" : "pointer",
        // Faster than transition-all; only animate transform + bg-color.
        transition: "transform 80ms ease-out, background-color 80ms ease-out",
        transform: pressed ? "scale(0.94)" : "scale(1)",
      }}
      className={className}
      {...rest}
    >
      {children}
    </button>
  );
});

export default DialerKey;
